import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Popover } from '@/components/chariot-ui';
import { Spinner } from '@/components/ui/spinner';
import { useModifyAccount } from '@/hooks';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWhoami } from '@/hooks/useWhoami';
import { queryClient } from '@/queryclient';
import { AccountMetadata } from '@/types';
import { cn } from '@/utils/classname';
import {
  formatRoleName,
  getFilteredRoleOptions,
  RoleName,
} from '@/utils/role.util';

interface InlineRoleEditorProps {
  /** The user's email (account member) */
  memberEmail: string;
  /** The user's current role */
  currentRole?: string;
  /** The user's account config */
  config: AccountMetadata;
  /** Optional customer email for impersonation context */
  customerEmail?: string;
}

/**
 * InlineRoleEditor renders the user's current role as a clickable chip
 * that opens a popover with role options for inline editing.
 *
 * Visibility is controlled by the parent component — this component
 * assumes the caller has already verified RBAC + admin permissions.
 *
 * @example
 * ```tsx
 * <InlineRoleEditor
 *   memberEmail="user@example.com"
 *   currentRole="readonly"
 *   config={account.config}
 *   customerEmail={customerEmail}
 * />
 * ```
 */
export const InlineRoleEditor: React.FC<InlineRoleEditorProps> = ({
  memberEmail,
  currentRole,
  config,
  customerEmail,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { data: whoami } = useWhoami();
  const { isSuperAdmin, isPraetorian } = useEntitlements();
  const roleOptions = getFilteredRoleOptions(
    whoami?.role,
    isSuperAdmin,
    memberEmail,
    isPraetorian
  );
  const { mutateAsync: linkAccount } = useModifyAccount(
    'link',
    customerEmail,
    false
  );

  const handleRoleChange = async (newRole: RoleName) => {
    if (newRole === currentRole) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      await linkAccount({
        username: memberEmail,
        config,
        role: newRole,
        silent: true,
      });

      // Invalidate account queries to refresh the table
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey as string[];
          return key[0] === 'MY' && key[1] === 'account';
        },
      });

      toast.success('Role updated', {
        description: `${memberEmail} is now ${formatRoleName(newRole)}.`,
      });
    } catch {
      toast.error('Failed to update role', {
        description: `Could not change role for ${memberEmail}. Please try again.`,
      });
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };

  const roleLabel = formatRoleName(currentRole);

  return (
    <Popover
      trigger={
        <button
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-sm transition-colors',
            'border-chariot-stroke bg-chariot-background-primary',
            'hover:border-chariot-brand-primary hover:bg-chariot-background-secondary',
            'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden',
            isOpen &&
              'border-chariot-brand-primary bg-chariot-background-secondary',
            isUpdating && 'pointer-events-none opacity-60'
          )}
          data-testid={`role-editor-trigger-${memberEmail}`}
          disabled={isUpdating}
          aria-label={`Change role for ${memberEmail}, currently ${roleLabel}`}
        >
          <span>{roleLabel}</span>
          {isUpdating ? (
            <Spinner size="xs" label="Updating role" />
          ) : (
            <ChevronDownIcon
              className={cn(
                'text-chariot-text-secondary size-3 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          )}
        </button>
      }
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      position="bottom"
      showArrow={false}
      contentClassName="p-1"
      offset={4}
    >
      <div
        className="flex min-w-[140px] flex-col"
        data-testid={`role-editor-options-${memberEmail}`}
      >
        <p className="text-chariot-text-secondary px-2 py-1 text-xs font-medium">
          Change role
        </p>
        {roleOptions.map(option => (
          <button
            key={option.value}
            onClick={() => handleRoleChange(option.value)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
              'hover:bg-chariot-background-secondary',
              option.value === currentRole
                ? 'text-chariot-brand-primary font-medium'
                : 'text-chariot-text-primary'
            )}
            data-testid={`role-option-${option.value}-${memberEmail}`}
            disabled={isUpdating}
          >
            <span
              className={cn(
                'size-1.5 rounded-full',
                option.value === currentRole
                  ? 'bg-chariot-brand-primary'
                  : 'bg-transparent'
              )}
            />
            {option.label}
          </button>
        ))}
      </div>
    </Popover>
  );
};
